// src/api/loginAPI.ts
import axios, { AxiosError } from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ?? "http://localhost:3001";

// Separate instance for auth requests (no token needed)
const authApiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 10000,
});

export interface LoginDTO {
  email: string;
  password: string;
}

export interface ApiResponse<T = unknown> {
  status: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string[]> | string;
  path?: string;
  method?: string;
  timestamp?: string;
}

export interface RegisterResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    role: string;
    uuid: string;
  };
}

export interface StoreInfo {
  store_id: string;
  name: string;
  currency: string;
  currencySymbol: string;
  timezone: string;
}

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    uuid: string;
    clientId?: string;
    clientTag?: 'restaurant' | 'retailer';
    enabledPaymentTypes?: string[];
    storeIds?: string[]; // Store IDs where user is assigned (e.g., ["STORE_001", "STORE_002"])
    storeNames?: string[]; // Store names for display
    stores?: StoreInfo[]; // Full store info with currency
    storeCurrency?: string; // Currency code (e.g., MYR, INR, USD) - first store's currency
    storeCurrencySymbol?: string; // Currency symbol (e.g., RM, ₹, $) - first store's symbol
    activeStoreId?: string; // Currently active store ID
    permissions: string[]; // User's effective permissions (legacy)
    effectivePermissions?: Array<{ // NEW: RBAC computed permissions from backend
      uuid: string;
      action: string;
      name: string;
      category: string;
    }>;
    roleIds?: number[]; // NEW: Numeric role IDs from RBAC
    additionalPermissionIds?: number[]; // NEW: Additional permission IDs
  };
}

export async function loginAPI(
  dto: LoginDTO
): Promise<ApiResponse<LoginResponse>> {
  try {
    const { data } = await authApiClient.post<ApiResponse<LoginResponse>>(
      "auth/login",
      dto
    );
    console.log("Login API response:", data);
    return data;
  } catch (error) {
    const err = error as AxiosError<ApiResponse>;

    if (err.response?.data) {
      // Backend always follows ApiResponse
      return err.response.data as ApiResponse<LoginResponse>;
    }

    return {
      status: false,
      message: "Network error. Please try again later.",
    };
  }
}

export async function adminLoginAPI(
  dto: LoginDTO
): Promise<ApiResponse<LoginResponse>> {
  try {
    const { data } = await authApiClient.post<ApiResponse<LoginResponse>>(
      "auth/admin/login",
      dto
    );
    console.log("Admin Login API response:", data);
    return data;
  } catch (error) {
    const err = error as AxiosError<ApiResponse>;

    if (err.response?.data) {
      // Backend always follows ApiResponse
      return err.response.data as ApiResponse<LoginResponse>;
    }

    return {
      status: false,
      message: "Network error. Please try again later.",
    };
  }
}

/**
 * Fetch current user profile with permissions
 * Used to refresh user data after page reload
 */
export async function getCurrentUserAPI(): Promise<ApiResponse<{ user: LoginResponse['user'] }>> {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      return {
        status: false,
        message: "No token found",
      };
    }

    const { data } = await authApiClient.get<ApiResponse<{ user: LoginResponse['user'] }>>(
      "auth/me",
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return data;
  } catch (error) {
    const err = error as AxiosError<ApiResponse>;

    if (err.response?.data) {
      return err.response.data as ApiResponse<{ user: LoginResponse['user'] }>;
    }

    return {
      status: false,
      message: "Network error. Please try again later.",
    };
  }
}
